import { ApiLocationForDashboard } from "../../../../shared/types/ApiLocationForDashboard";
import { ActivityStatus } from "../../../../shared/enums/ActivityStatus";
import { StateStatus } from "../../../../shared/enums/StateStatus";
import { Copies } from "../../../../shared/hooks/useCopies";
import { DashboardActionType } from "../../../../shared/enums/DashboardActionType";
import { isTabletalkProgram } from "../../../../shared/utils";
import { generatePath } from "react-router-dom";
import { MyExpRoute } from "../../routes/types";
import { NavigateFunction } from "react-router";
import { ProgramType } from "../../../../shared/enums/ProgramType";
import { ApiProgramParticipantForDashboard } from "shared/types/ApiProgramParticipantForDashboard";
import { ORG_ADMIN_PARTICIPANT_TYPES } from "shared/enums/ProgramParticipantType";
import { ApiEventParticipantForDashboard } from "../../../../shared/types/ApiEventParticipantForDashboard";
import ContextEntityType from "../../../../shared/enums/ContextEntityType";
import { ApiDashboardAction } from "../../../../shared/types/ApiDashboardAction";
import DateTimeHelper from "shared/utils/dateTimeHelper";

export function getDisplayLocationStr(location?: ApiLocationForDashboard) {
  if (!location) {
    return null;
  }
  return [location.name, location.venueName, location.locality].filter(i => !!i).join(", ");
}

export function getTimelineActivityStatusIndicatorClass({ activityStatus }: { activityStatus?: ActivityStatus }) {
  switch (activityStatus) {
    case ActivityStatus.ACTION_REQUIRED:
      return "program_timeline_actions_actionRequired";
    case ActivityStatus.COMPLETED:
      return "program_timeline_actions_completed";
    case ActivityStatus.ON_GOING:
      return "program_timeline_actions_onGoing";
    case ActivityStatus.NOT_YET_TIME:
    case ActivityStatus.NOT_APPLICABLE:
      return "program_timeline_actions_notYetTime";
    default:
      return "";
  }
}

export function getProgramStateIndicatorClass({ status }: { status: StateStatus }) {
  switch (status) {
    case StateStatus.ELAPSED:
      return "program_timeline_actions_completed";
    case StateStatus.ON_GOING:
      return "program_timeline_actions_onGoing";
    case StateStatus.NOT_YET_TIME:
      return "program_timeline_actions_notYetTime";
    default:
      return "";
  }
}

export function getTimelineDisplayDate({ actionObj, copies }: { actionObj: ApiDashboardAction; copies: Copies }) {
  const startDate = actionObj.time?.timeFrom
    ? DateTimeHelper.formatDate({
        startDate: actionObj.time.timeFrom,
        timeZone: actionObj.time.timeZoneDisplayStr,
        option: {
          showWeekDay: true,
          showYear: false,
          showTime: true
        }
      })
    : null;
  const endDate = actionObj.time?.timeTo
    ? DateTimeHelper.formatDate({
        startDate: actionObj.time.timeTo,
        timeZone: actionObj.time.timeZoneDisplayStr,
        option: {
          showWeekDay: true,
          showYear: false,
          showTime: true
        }
      })
    : null;
  switch (actionObj.stateStatus) {
    case StateStatus.NOT_YET_TIME:
      return startDate ? copies.get("starts.on", { 0: startDate }) : "";
    case StateStatus.ON_GOING:
      return endDate ? copies.get("by", { 0: endDate }) : "";
    case StateStatus.ELAPSED:
      return endDate ? copies.get("ended.on", { 0: endDate }) : "";
    default:
      return "";
  }
}

function getOrgAdminProgramParticipantId(programParticipants?: Array<ApiProgramParticipantForDashboard>) {
  return programParticipants?.find(p => ORG_ADMIN_PARTICIPANT_TYPES.includes(p.programParticipantType))?.id;
}

export function navigateBasedOnDashboardActionType(props: {
  navigate: NavigateFunction;
  dashboardActionType: DashboardActionType;
  customData?: {
    entityId?: string;
    programOrganizationId?: string;
    programType?: ProgramType;
    programId?: string;
    programParticipants?: Array<ApiProgramParticipantForDashboard>;
    eventParticipant?: ApiEventParticipantForDashboard;
    contextEntityId?: string;
    contextEntityType?: ContextEntityType;
  };
}) {
  switch (props.dashboardActionType) {
    case DashboardActionType.COMPLETE_PROFILE:
      props.navigate({
        pathname: isTabletalkProgram(props.customData?.programType)
          ? generatePath(MyExpRoute.TABLETALK_PROFILE.path, { programParticipantId: props.customData?.entityId })
          : generatePath(MyExpRoute.PROFILE.path, { programParticipantId: props.customData?.entityId })
      });
      break;
    case DashboardActionType.COMPLETE_ORG_PROFILE:
    case DashboardActionType.COMPLETE_EXHIBITOR_PROFILE:
      props.navigate({
        pathname: generatePath(MyExpRoute.ORGANIZATION_PROFILE.path, {
          programOrganizationId: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.UPDATE_EXHIBITOR_PROFILE:
      props.navigate({
        pathname: generatePath(MyExpRoute.EVENT_ORGANIZATION_PROFILE.path, {
          eventOrganizationId: props.customData?.eventParticipant?.eventOrganizationId,
          eventParticipantId: props.customData?.eventParticipant?.id
        })
      });
      break;
    case DashboardActionType.REQUEST_BY_PARTICIPANT:
    case DashboardActionType.REQUEST_BY_EXHIBITOR:
    case DashboardActionType.REQUEST_AND_OPT_IN_BY_PARTICIPANT:
    case DashboardActionType.OPT_IN_BY_PARTICIPANT:
    case DashboardActionType.REQUEST_AND_OPT_IN_OF_ORGANIZATION_BY_PARTICIPANT:
      props.navigate(generatePath(MyExpRoute.SELECTION.path, { programParticipantId: props.customData?.entityId }));
      break;
    case DashboardActionType.REQUEST_BY_ORG_ADMIN:
    case DashboardActionType.REQUEST_AND_OPT_IN_BY_ORG_ADMIN:
    case DashboardActionType.REQUEST_AND_OPT_IN_OF_ORGANIZATION_BY_ORG_ADMIN:
    case DashboardActionType.OPT_IN_BY_ORG_ADMIN:
      props.navigate(
        generatePath(MyExpRoute.ORG_ADMIN_SELECTION.path, { programParticipantId: props.customData?.entityId })
      );
      break;
    case DashboardActionType.COMPLETE_ORG_PARTICIPANT_PROFILE:
      props.navigate({
        pathname: generatePath(`${MyExpRoute.ORGANIZATION.path}/${MyExpRoute.ORGANIZATION.PROFILE_LIST.path}`, {
          orgAdminProgramParticipantId: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.ATTEND_SCHEDULED_MEETINGS:
    case DashboardActionType.ATTEND_SCHEDULED_ACTIVITIES:
    case DashboardActionType.ATTEND_SCHEDULED_MEETINGS_EVENT_PARTICIPANT:
    case DashboardActionType.VIEW_ATTEND_SCHEDULE:
      const eventParticipantId = props.customData?.entityId;
      props.navigate({
        pathname: generatePath(
          `${MyExpRoute.PARTICIPANT_SCHEDULE.path}/${MyExpRoute.PARTICIPANT_SCHEDULE.MEETING_DETAIL_DEFAULT.path}`,
          {
            entityId: eventParticipantId,
            contextEntityType: ContextEntityType.EVENT_PARTICIPANT
          }
        )
      });
      break;
    case DashboardActionType.VIEW_ORGANIZATION_CONFIRMED_MEETINGS:
    case DashboardActionType.ORG_SCHEDULE:
      props.navigate({
        pathname: generatePath(
          `${MyExpRoute.PARTICIPANT_SCHEDULE.path}/${MyExpRoute.PARTICIPANT_SCHEDULE.MEETING_DETAIL_DEFAULT.path}`,
          {
            entityId: props.customData?.entityId,
            contextEntityType: ContextEntityType.PROGRAM_ORGANIZATION
          }
        )
      });
      break;
    case DashboardActionType.VIEW_ORGANIZATION_CONFIRMED_MEETINGS_CONNECTIONS:
    case DashboardActionType.ORG_SCHEDULE_CONNECTIONS:
      props.navigate({
        pathname: generatePath(MyExpRoute.ORGANIZATION_SCHEDULE.path, {
          programOrganizationId: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.ACCEPT_MEETINGS:
    case DashboardActionType.ACCEPT_ADDITIONAL_MEETINGS:
      props.navigate({
        pathname: generatePath(MyExpRoute.ACCEPT_MEETINGS.path, { programParticipantId: props.customData?.entityId })
      });
      break;
    case DashboardActionType.VIEW_PARTICIPANT_SCHEDULED_MEETINGS:
    case DashboardActionType.VIEW_PARTICIPANT_CONFIRMED_MEETINGS:
    case DashboardActionType.VIEW_PARTICIPANT_ADDITIONAL_SCHEDULED_MEETINGS:
      //Accept meeting listing for org admin
      props.navigate({
        pathname: generatePath(
          `${MyExpRoute.ORGANIZATION.path}/${MyExpRoute.ORGANIZATION.AGENDA_PARTICIPATION_LIST.path}`,
          {
            orgAdminProgramParticipantId: props.customData?.entityId
          }
        )
      });
      break;
    case DashboardActionType.ASSIGN_MEETING_REP:
      props.navigate({
        pathname: generatePath(MyExpRoute.ASSIGN_MEETING_REP.path, {
          orgAdminPPIDInMeetingRepAssign: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.SHARE_RECEIPTS:
      props.navigate({
        pathname: generatePath(`${MyExpRoute.REIMBURSEMENT.path}`, {
          programParticipantId: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.SHARE_FEEDBACK:
      props.navigate({
        pathname: generatePath(MyExpRoute.FEEDBACK.path, {
          eventParticipantId: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.FEEDBACK:
      props.navigate({
        pathname: generatePath(MyExpRoute.FEEDBACK.path, {
          eventParticipantId: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.VIEW_PARTICIPANT_FEEDBACK:
      props.navigate({
        pathname: generatePath(`${MyExpRoute.ORGANIZATION.path}/${MyExpRoute.ORGANIZATION.FEEDBACK_LIST.path}`, {
          orgAdminProgramParticipantId: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.MANAGE_TICKETS:
      props.navigate({
        pathname: `/${MyExpRoute.MANAGE_BULK_TICKET.path}`
      });
      break;
    case DashboardActionType.ADDITIONAL_CHARGE:
      props.navigate({
        pathname: `/${MyExpRoute.ADDITIONAL_CHARGE.path}`
      });
      break;
    case DashboardActionType.PURCHASE_TICKET:
      props.navigate({
        pathname: `/${MyExpRoute.TICKET_PURCHASE.path}`
      });
      break;
    case DashboardActionType.MANAGE_PARTICIPANTS:
    case DashboardActionType.INSUFFICIENT_MEETING_REP:
      props.navigate({
        pathname: generatePath(`/${MyExpRoute.MANAGE_PARTICIPANT.path}`, {
          programParticipantId: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.COMPLETE_ACTIVITY_PROFILE:
      props.navigate({
        pathname: `/${generatePath(MyExpRoute.WORKFLOW.path, {
          programParticipantId: props.customData?.entityId
        })}/${MyExpRoute.WORKFLOW.ASSOCIATED_PROGRAM_ACTIVITIES.path}`
      });
      break;
    case DashboardActionType.COMPLETE_ACTIVITY_SELECTION:
      props.navigate({
        pathname: generatePath(`/${MyExpRoute.ASSOCIATED_PROGRAM_ACTIVITY_LIST.path}`, {
          eventParticipantId: props.customData?.entityId
        })
      });
      break;
    case DashboardActionType.EXHIBITOR_LISTING:
      props.navigate({
        pathname: generatePath(MyExpRoute.EXHIBITORS_LIST.path, {
          actorId: props.customData?.eventParticipant?.id,
          actorEntityType: ContextEntityType.EVENT_PARTICIPANT,
          contextId: props.customData?.contextEntityId,
          contextType: props.customData?.contextEntityType
        })
      });
      break;
    case DashboardActionType.SPONSOR_LISTING:
      props.navigate({
        pathname: generatePath(MyExpRoute.SPONSORS_LIST.path, {
          actorId: props.customData?.eventParticipant?.id,
          actorEntityType: ContextEntityType.EVENT_PARTICIPANT,
          contextId: props.customData?.contextEntityId,
          contextType: props.customData?.contextEntityType
        })
      });
      break;
    case DashboardActionType.ADD_TO_SCHEDULE_BY_ATTENDEE:
      props.navigate({
        pathname: generatePath(MyExpRoute.EXHIBITORS_LIST.path, {
          actorId: props.customData?.entityId,
          actorEntityType: ContextEntityType.PROGRAM_PARTICIPANT,
          contextId: props.customData?.contextEntityId,
          contextType: props.customData?.contextEntityType
        })
      });
      break;
    case DashboardActionType.COMPLETE_PARTICIPANT_SHORTLISTING:
      props.navigate({
        pathname: `/${generatePath(MyExpRoute.WORKFLOW.path, {
          programParticipantId: getOrgAdminProgramParticipantId(props.customData?.programParticipants)
        })}/${MyExpRoute.WORKFLOW.ASSOCIATED_PROGRAM_SHORTLIST.path}`
      });
      break;
    case DashboardActionType.ACCEPT_SCHEDULED_ACTIVITY:
      props.navigate({
        pathname: `/${generatePath(MyExpRoute.WORKFLOW.path, {
          programParticipantId: getOrgAdminProgramParticipantId(props.customData?.programParticipants)
        })}/${MyExpRoute.WORKFLOW.ASSOCIATED_PROGRAM_SCHEDULE.path}`
      });
      break;
    case DashboardActionType.MANAGE_SCHEDULED_ACTIVITIES:
      props.navigate({
        pathname: `/${generatePath(MyExpRoute.WORKFLOW.path, {
          programParticipantId: getOrgAdminProgramParticipantId(props.customData?.programParticipants)
        })}/${MyExpRoute.WORKFLOW.ASSOCIATED_PROGRAM_MARK_ATTENDANCE.path}`
      });
      break;
    case DashboardActionType.MARK_ACTIVITY_ATTENDANCE:
      props.navigate({
        pathname: `/${generatePath(MyExpRoute.WORKFLOW.path, {
          programParticipantId: getOrgAdminProgramParticipantId(props.customData?.programParticipants)
        })}/${MyExpRoute.WORKFLOW.ASSOCIATED_PROGRAM_MARK_ATTENDANCE.path}`
      });
      break;
    case DashboardActionType.CONTACT:
      props.navigate({
        pathname: generatePath(MyExpRoute.CONTACTS.path, {
          eventParticipantId: props.customData?.eventParticipant?.id
        })
      });
      break;
    case DashboardActionType.LEAD_CAPTURE_ADMIN:
      props.navigate({
        pathname: generatePath(MyExpRoute.LEADS.path, {
          eventParticipantId: props.customData?.eventParticipant?.id
        })
      });
      break;
  }
}
